import React from "react"
import { useStaticQuery, graphql } from "gatsby"
import styled from "styled-components"
import { rem, rgba } from "polished"
import { ImagesCarousel } from "@outdoormap/gatsby-plugin-brand-sites"

import Icon from "../icon"
import { Label3, Label4 } from "../styled/label"
import { Heading1 } from "../styled/heading"
import { Paragraph, ParagraphLarge } from "../styled/paragraph"
import { ReactComponent as SvgCalendar } from "../../assets/images/icons/calendar.svg"

export default function EventHero({
  data: { title, tagline, openTimes, location, images } = {},
}) {
  const { translations } = useStaticQuery(graphql`
    query {
      translations: translationsJson {
        global {
          event
          location
          openingHours
        }
      }
    }
  `)

  return (
    <Container>
      <Inner>
        <Info>
          <Label>
            <Icon
              iconSvg={<SvgCalendar />}
              className="-icon"
              aria-hidden="true"
            />

            <Label3
              dangerouslySetInnerHTML={{ __html: translations.global.event }}
            />
          </Label>

          {title && <Title dangerouslySetInnerHTML={{ __html: title }} />}

          {!!openTimes?.length && (
            <>
              <Label4
                as="h2"
                dangerouslySetInnerHTML={{
                  __html: translations.global.openingHours,
                }}
              />

              {openTimes.map((time, i) => (
                <Date key={i} as="time" dateTime={time.opensIso}>
                  <span dangerouslySetInnerHTML={{ __html: time.opens }} />
                  &nbsp;-&nbsp;
                  <span dangerouslySetInnerHTML={{ __html: time.closes }} />
                </Date>
              ))}
            </>
          )}

          {tagline && <Tagline dangerouslySetInnerHTML={{ __html: tagline }} />}

          {location && (
            <Location>
              <Label4
                as="h2"
                dangerouslySetInnerHTML={{
                  __html: translations.global.location,
                }}
              />

              <span
                className="-value"
                dangerouslySetInnerHTML={{ __html: location }}
              />
            </Location>
          )}
        </Info>

        <Images>
          <ImagesCarousel data={images} title={title} />
        </Images>
      </Inner>
    </Container>
  )
}

const Container = styled.header`
  ${({ theme }) => theme.grid.container()}
`

const Inner = styled.div`
  display: flex;
  flex-wrap: wrap;
  overflow: hidden;
  border-radius: ${rem(8)};
  background-color: ${({ theme }) => theme.colors.cream};
`

const Info = styled.div`
  width: 50%;
  padding: ${rem(40)} ${rem(50)};
  display: flex;
  flex-direction: column;

  @media ${({ theme }) => theme.mq.largeDown} {
    width: 100%;
  }

  @media ${({ theme }) => theme.mq.smallDown} {
    padding: ${rem(14)};
  }

  @media ${({ theme }) => theme.mq.xsmallDown} {
    padding: ${rem(10)};
  }

  ${Label4} {
    margin-bottom: ${rem(15)};
    font-size: ${rem(16)};
  }

  h1 {
    word-break: normal;
    font-size: ${rem(48)};

    @media ${({ theme }) => theme.mq.mediumDown} {
      font-size: ${rem(45)};
    }

    @media ${({ theme }) => theme.mq.smallDown} {
      font-size: ${rem(36)};
    }

    @media ${({ theme }) => theme.mq.xsmallDown} {
      font-size: ${rem(28)};
    }
  }
`

const Label = styled.div`
  margin-bottom: 1.5em;
  display: inline-flex;
  align-items: center;
  color: ${({ theme }) => theme.colors.metallic};

  ${Label3} {
    margin-left: 0.75em;
    color: inherit;
  }

  .-icon {
    @media ${({ theme }) => theme.mq.smallDown} {
      width: 2em;
      height: 2em;
    }
  }
`

const Date = styled(Paragraph)`
  margin-bottom: 0.875em;
  color: ${({ theme }) => theme.colors.metallic};
`

const Title = styled(Heading1)`
  max-width: ${rem(420)};
  margin-bottom: 0.5em;
`

const Tagline = styled(ParagraphLarge)`
  color: ${({ theme }) => theme.colors.metallic};
`

const Location = styled.div`
  margin-top: auto;
  padding-top: 3.6em;
  position: relative;
  color: ${({ theme }) => theme.colors.metallic};

  &::before {
    content: "";
    width: 100%;
    height: 1px;
    background-color: ${({ theme }) => rgba(theme.colors.gold, 0.2)};
    position: absolute;
    top: 1.8em;
    left: 0;
  }

  > .-value {
    margin-top: 0.5em;
    display: block;
    font-size: 0.875em;
  }
`

const Images = styled.div`
  width: 50%;

  @media ${({ theme }) => theme.mq.largeDown} {
    width: 100%;
    aspect-ratio: 16 / 9;
  }
`
